<template>
  <li
    :class="['skill-dive-lab-row', isLoading && 'skill-dive-lab-row--disabled']"
    @click="goToContent"
  >
    <div
      v-if="content.difficulty"
      class="skill-dive-lab-row__difficulty-learning-area-icon"
    >
      <DifficultyLearningAreaIcon
        :difficulty="content.difficulty"
        :learningArea="learningAreaName"
      />
    </div>
    <div class="skill-dive-lab-row__info-wrapper">
      <div class="skill-dive-lab-row__info">
        <div class="skill-dive-lab-row__info-corner-content-wrapper">
          <div class="skill-dive-lab-row__info-corner-content">
            <AddedDaysAgo
              v-if="content.published_date && showAddedDaysAgo"
              :label="addedTimeAgoLabel"
              class="skill-dive-lab-row__info-added-days-ago"
            />
            <YearPill
              v-if="isVulnerability && content.year"
              :year="content.year"
              :straighten-corner="
                [
                  'top-left bottom-right',
                  content.published_date && showAddedDaysAgo && 'top-right',
                ].join(' ')
              "
              :class="[
                'skill-dive-lab-row__info-year',
                content.published_date &&
                  showAddedDaysAgo &&
                  'skill-dive-lab-row__info-year--long',
              ]"
            />
          </div>
        </div>
        <SkillDiveLabRowBrief
          :content="content"
          :locked="lockedContent(content)"
          :isLoading="isLoading"
          :isVulnerability="isVulnerability"
          @toggle-expand="toggleExpand"
        />
        <transition name="skill-dive-lab-row__info-details--expand">
          <div v-if="isExpanded" class="skill-dive-lab-row__info-details">
            <SeparatorBorder
              gradient
              class="skill-dive-lab-row__info-details-separator"
            />
            <ContentRowDetails
              :content="content"
              :locked="lockedContent(content)"
              :isLoading="isLoading"
              :isVulnerability="isVulnerability"
              @tag-selected="tag => $emit('tag-selected', tag)"
            />
          </div>
        </transition>
      </div>
    </div>
  </li>
</template>
<script>
import SkillDiveLabRowBrief from './SkillDiveLabRowBrief.vue'
import ContentRowDetails from './ContentRowDetails.vue'
import { getLearningAreaColor, getLearningAreaName } from '@/helpers'
import DifficultyLearningAreaIcon from '@/components/properties/DifficultyLearningAreaIcon.vue'
import YearPill from '@/components/properties/YearPill.vue'
import AddedDaysAgo from '@/components/search/content-rows/AddedDaysAgo.vue'
import { collectionCommons } from '@/helpers/mixins'
import SeparatorBorder from '@/components/shared/SeparatorBorder.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'SkillDiveLabRow',
  components: {
    SkillDiveLabRowBrief,
    ContentRowDetails,
    AddedDaysAgo,
    DifficultyLearningAreaIcon,
    YearPill,
    SeparatorBorder,
  },
  mixins: [collectionCommons],
  emits: ['tag-selected'],
  props: {
    content: {
      type: Object,
      required: true,
    },
    allExpanded: {
      type: Boolean,
      default: false,
    },
    learningAreasAllowed: {
      type: Array,
      default: () => [],
    },
    updateExpanded: {
      type: Boolean,
      default: false,
    },
    isolatedContent: {
      type: Boolean,
      default: true,
    },
  },
  data: () => {
    return {
      isExpanded: false,
      isLoading: false,
      checkoutUrl: `https://${process.env.VUE_APP_CHECKOUT_URL}`,
      skillDiveEngage: `https://${process.env.VUE_APP_SKILL_DIVE_ENGANGE}`,
    }
  },
  computed: {
    learningAreaName() {
      return getLearningAreaName(this.content) // Design limitation (only one Hexagon)
    },
    learningAreaColor() {
      return getLearningAreaColor(this.content)
    },
    isVulnerability() {
      return (
        this.content.id === CONSTANTS.LABS_COLLECTIONS.VULNERABILITIES ||
        !!this.content.cve
      )
    },
  },
  watch: {
    allExpanded(value) {
      this.isExpanded = this.updateExpanded ? value : this.isExpanded
    },
  },
  methods: {
    async goToContent() {
      this.$emit('content-clicked')
      if (this.lockedContent(this.content)) {
        if (this.isSkillDiveB2CUser) {
          this.$router.push({
            name: 'user-dashboard',
            params: {
              mode: 'subscriptions',
            },
          })
        } else if (this.isBusinessUser && !this.isAccOwnerOrTeamManager) {
          if (!this.isLoading) {
            this.isLoading = true
            await this.requestAddonLicense('skill-dive')
            this.isLoading = false
          }
        } else {
          window.location.href = this.isAccOwnerOrTeamManager
            ? this.skillDiveEngage
            : this.checkoutUrl
        }
      } else {
        let route = 'lab-page'
        const params = {
          labId: this.content.uuid || this.content.objectID,
        }
        if (!this.isolatedContent) {
          params.id =
            this.currentCollection.id || this.currentCollection.objectID
          route = `${this.$route.name}-lab`
        }
        this.$router.push({
          name: route,
          params,
        })
      }
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded
      this.$emit('row-collapsed')
    },
  },
}
</script>
<style scoped lang="scss">
.skill-dive-lab-row {
  display: flex;
  position: relative;
  &__difficulty-learning-area-icon {
    position: absolute;
    display: flex;
    height: 100%;
    z-index: 1;
    > div {
      height: 85px;

      @include mobile {
        height: 99px;
      }
    }
  }

  &--disabled {
    cursor: not-allowed;
  }

  &__info {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    color: var(--white);
    background: var(--glass-mid-gray);
    min-height: 85px;
    position: relative;
    cursor: pointer;
    border-radius: 10px;
    padding: 10px 10px 10px 40px;
    &:hover {
      background: var(--glass-light-gray);
    }
    &:active {
      background: var(--glass-dark);
    }
    @include mobile {
      height: auto;
      padding: 17px 17px 17px 40px;
    }
    &-wrapper {
      width: 100%;
      border-radius: 10px;
      background: linear-gradient(
          0deg,
          rgba(0, 0, 0, 0.2) 0%,
          rgba(0, 0, 0, 0.2) 100%
        ),
        #222;
      box-shadow: 0px 30px 48px 0px rgba(0, 0, 0, 0.25);

      margin-left: 60px;
    }
    &--expand {
      height: auto;
    }
    &-corner {
      &-content {
        display: flex;
        flex-flow: row-reverse;
        &-wrapper {
          position: absolute;
          top: 0;
          right: 0;
        }
      }
    }
    &-year {
      &--long {
        position: absolute;
        padding-right: calc(100% + 10px);
        z-index: 1;
      }
    }
    &-added-days-ago {
      height: 24px;
      z-index: 2;
    }
    &-details {
      max-height: 1000px;
      opacity: 1;
      &-separator {
        margin: 5px 0;
      }
      &--expand {
        &-enter-active,
        &-leave-active {
          transition: all 0.2s ease-in-out;
        }
        &-enter,
        &-leave-to {
          max-height: 0px;
          opacity: 0;
        }
      }
    }
  }
}
</style>
